import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useForm } from "react-hook-form";
import { isNull, cloneDeep, isEmpty, isEqual } from "lodash";
import stylesV2 from "./ConfirmInsurance.module.scss";
import stylesV3 from "./ConfirmInsuranceV2.module.scss";
import { ConfirmInsuranceForm } from "./components/InsuranceForm";
import { InsuranceFormData, RelationToPatient } from "./types";
import { useProgressGuard } from "./hooks/useProgressGuard";
import deleteIcon from "images/delete-icon.svg";
import addCircle from "images/add-circle.svg";
import errorIcon from "images/errorIcon.svg";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import { useErrorToast } from "features/patientDashboard/hooks/useErrorToast";
import {
  setInsuranceData,
  setCurrentInsurance,
  setIsEditForm,
  setShowAddFooter,
  selectInsuranceData,
  selectCurrentInsurance,
  selectIsEditForm,
  selectShowAddFooter,
} from "features/insuranceDataSlice";
import { getInsuranceCarriersRequest } from "api/getInsuranceCarriersRequest";
import { useSchedulingABVariant } from "features/scheduling/utils/useSchedulingABVariant";

const CancelToken = axios.CancelToken;

export const ConfirmInsurance: React.FC<{
  defaultValues: InsuranceFormData | null;
  isSubmit: boolean;
  getInsuranceValues: any;
  currentPatient: any;
  version?: number;
  disableStoredFormData?: boolean;
  alignSsnTooltipToInput?: boolean;
  disableProgressGuard?: boolean;
}> = ({
  defaultValues,
  isSubmit,
  currentPatient,
  version,
  disableStoredFormData = false,
  alignSsnTooltipToInput = false,
  disableProgressGuard = false,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const [insuranceCarriers, setInsuranceCarriers] =
    React.useState<Array<string> | null>(null);
  const { currentAppointment } = useCurrentAppointment();
  const { showErrorToast } = useErrorToast();
  const [executeOnce, setExecuteOnce] = useState(false);
  const hasInitializedForm = React.useRef(false);
  const dispatch = useDispatch();
  const insuranceFormDataArr = useSelector(selectInsuranceData);
  const isShowFooter = useSelector(selectShowAddFooter);
  const [isForm, setIsForm] = useState(false);
  const [isFirstCard, setIsFirstCard] = useState(false);
  const [isSecondCard, setIsSecondCard] = useState(false);
  const [isErrorFirstFields, setErrorFirstFields] = useState(false);
  const [isErrorSecondFields, setErrorSecondFields] = useState(false);
  const [addAnotherCard, setAddAnotherCard] = useState(false);
  const [isFormRemove, setIsFormRemove] = useState(false);
  const currentInsurance = useSelector(selectCurrentInsurance);
  const isEditForm = useSelector(selectIsEditForm);
  const insuranceData = insuranceFormDataArr[currentInsurance];
  const errorMessageCard = "Enter Required Fields";

  const checkInsAddress = (insurance: InsuranceFormData) => {
    if (insurance.isPrimaryPolicyHolder === "no") {
      if (
        insurance.address1 &&
        insurance.state &&
        insurance.city &&
        insurance.zipCode &&
        insurance.subscriberBirthSex
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const checkFormInsurance = useCallback((insurance: InsuranceFormData) => {
    const requiresAdditionalSubscriberDetails =
      insurance.isPrimaryPolicyHolder === "no";

    if (
      insurance &&
      insurance.insuranceCarrier &&
      insurance.isPrimaryPolicyHolder &&
      insurance.subscriberFirstName &&
      insurance.subscriberLastName &&
      insurance.subscriberId &&
      insurance.subscriberDateOfBirth &&
      checkInsAddress(insurance) &&
      (insurance.insuranceCarrier !== "VSP" || insurance.subscriberSsn) &&
      (!requiresAdditionalSubscriberDetails || insurance.relationToPatient)
    ) {
      if (insurance.insuranceCarrier === "Other Insurance") {
        return insurance.otherInsuranceName ? true : false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }, []);

  const getValidInsurance = useCallback(
    (insuranceDataArr) => {
      // eslint-disable-next-line array-callback-return
      let isValidForm = false;
      let isValidFormTemp = false;
      let i = 0;
      for (const insurance of insuranceDataArr) {
        if (!isEmpty(insurance)) {
          isValidFormTemp = checkFormInsurance(insurance) ? true : false;
        }

        if (i === 0) {
          isValidForm = isValidFormTemp;
          setErrorFirstFields(isValidForm);
        } else {
          setErrorSecondFields(isValidFormTemp);
        }
        i++;
      }
    },
    [checkFormInsurance]
  );

  React.useEffect(() => {
    // if (!currentAppointment) return;
    const cancelSource = CancelToken.source();

    const getCarriers = async () => {
      try {
        const carriers = await getInsuranceCarriersRequest({
          cancelToken: cancelSource.token,
        });
        setInsuranceCarriers(carriers);
      } catch (e) {
        // TODO: There is a race condition in axios's implementation of cancel tokens
        // that can cause the thrown object to not contain the relevant state. This should
        // be fixed in the next release of axios (see https://github.com/axios/axios/pull/3305)
        if (axios.isCancel(cancelSource.token.reason)) {
          return;
        }
        showErrorToast();
      }
    };

    getCarriers();

    return () => {
      cancelSource.cancel("UNMOUNTED");
    };
  }, [showErrorToast, setInsuranceCarriers, currentAppointment]);

  const resetForm = () => {
    setValue("insuranceCarrier", "");
    setValue("otherInsuranceName", "");
    setValue("subscriberFirstName", "");
    setValue("subscriberLastName", "");
    setValue("subscriberId", "");
    setValue("subscriberDateOfBirth", "");
    setValue("isPrimaryPolicyHolder", "");
    setValue("relationToPatient", undefined);
    setValue("subscriberBirthSex", "");
    setValue("address1", "");
    setValue("address2", "");
    setValue("state", "");
    setValue("city", "");
    setValue("zipCode", "");
    setValue("subscriberSsn", "");
  };

  const onNext = (_data: InsuranceFormData) => {
    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
    // eslint-disable-next-line array-callback-return
    [getValues()].map((insurance: InsuranceFormData) => {
      insuranceDataArr[currentInsurance] = createInsuranceObj(insurance);
    });
    dispatch(setInsuranceData(insuranceDataArr));
    let isFormValid = false;
    for (const insurance of insuranceDataArr) {
      if (!isEmpty(insurance)) {
        isFormValid = checkFormInsurance(insurance) ? true : false;
        if (!isFormValid) break;
      }
    }
    dispatch(setIsEditForm(true));
  };

  const getInsuranceValues1 = (formData?: Partial<InsuranceFormData>) => {
    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
    const nextInsuranceData = formData || getValues();
    // eslint-disable-next-line array-callback-return
    [nextInsuranceData].map((insurance: InsuranceFormData) => {
      insuranceDataArr[currentInsurance] = createInsuranceObj(insurance);
    });
    // Avoid re-dispatching identical form state, which can cause a render loop
    // when controlled inputs re-emit their current value during mount/update.
    if (!isEqual(insuranceDataArr, insuranceFormDataArr)) {
      dispatch(setInsuranceData(insuranceDataArr));
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    getValues,
    clearErrors,
    control,
    trigger,
    watch,
  } = useForm<Partial<InsuranceFormData>>({
    defaultValues: isNull(defaultValues) ? {} : {},
    mode: "onChange",
  });
  useProgressGuard({
    field: "insurance",
    isSubmitting,
    disabled: disableProgressGuard,
  });

  const insuranceFormId = "Checkin-InsuranceForm";

  const checkFormEmpty = () => {
    return isEmpty(insuranceFormDataArr[0]) && isEmpty(insuranceFormDataArr[1]);
  };

  const shouldShowAddAnotherInsurance =
    currentInsurance === 0 &&
    !isEmpty(insuranceFormDataArr[0]) &&
    isEmpty(insuranceFormDataArr[1]) &&
    checkFormInsurance(insuranceFormDataArr[0]);

  React.useEffect(() => {
    if (disableStoredFormData && !hasInitializedForm.current) {
      resetForm();
      setExecuteOnce(true);
    } else if (!isForm && insuranceData && !executeOnce) {
      setValue("insuranceCarrier", insuranceData?.insuranceCarrier);
      if (insuranceData?.insuranceCarrier === "Other Insurance")
        setValue("otherInsuranceName", insuranceData?.otherInsuranceName);
      setValue("subscriberFirstName", insuranceData?.subscriberFirstName);
      setValue("subscriberLastName", insuranceData?.subscriberLastName);
      setValue("subscriberId", insuranceData?.subscriberId);
      setValue("subscriberDateOfBirth", insuranceData?.subscriberDateOfBirth);
      setValue("isPrimaryPolicyHolder", insuranceData?.isPrimaryPolicyHolder);
      setValue("relationToPatient", insuranceData?.relationToPatient);
      setValue("subscriberBirthSex", insuranceData?.subscriberBirthSex);
      setValue("address1", insuranceData?.address1);
      setValue("address2", insuranceData?.address2);
      setValue("state", insuranceData?.state);
      setValue("city", insuranceData?.city);
      setValue("zipCode", insuranceData?.zipCode);

      setValue("subscriberSsn", insuranceData?.subscriberSsn);
      setExecuteOnce(true);
      if (!isEmpty(insuranceFormDataArr[0]) && currentInsurance === 1) {
        setIsFirstCard(true);
      } else {
        setIsFirstCard(false);
      }
      if (!isEmpty(insuranceFormDataArr[1]) && currentInsurance === 0) {
        setIsSecondCard(true);
      } else {
        setIsSecondCard(false);
      }
    }
    hasInitializedForm.current = true;
    const shouldShowAddAnotherCard =
      (isShowFooter &&
        isEmpty(insuranceFormDataArr[0]) &&
        isEmpty(insuranceFormDataArr[1])) ||
      (isEditForm &&
        (isEmpty(insuranceFormDataArr[0]) || isEmpty(insuranceFormDataArr[1]))) ||
      shouldShowAddAnotherInsurance;

    setAddAnotherCard(shouldShowAddAnotherCard);

    if (isEditForm) {
      isEmpty(insuranceFormDataArr[1])
        ? setIsFormRemove(false)
        : setIsFormRemove(true);
    }

    getValidInsurance(insuranceFormDataArr);
  }, [
    disableStoredFormData,
    setValue,
    currentInsurance,
    insuranceFormDataArr,
    insuranceData,
    executeOnce,
    isShowFooter,
    isEditForm,
    isForm,
    shouldShowAddAnotherInsurance,
    getValidInsurance,
  ]);

  const refStr = "#";

  const createInsuranceObj = (insurance: InsuranceFormData) => {
    if (isEmpty(insurance)) return {};
    return {
      subscriberFirstName: insurance.subscriberFirstName,
      subscriberLastName: insurance.subscriberLastName,
      insuranceCarrier: insurance.insuranceCarrier,
      otherInsuranceName: insurance.otherInsuranceName,
      subscriberId: insurance.subscriberId,
      subscriberDateOfBirth: insurance.subscriberDateOfBirth,
      isPrimaryPolicyHolder: insurance.isPrimaryPolicyHolder,
      subscriberSsn: insurance.subscriberSsn,
      relationToPatient:
        insurance.relationToPatient ||
        (insurance.isPrimaryPolicyHolder === "yes"
          ? RelationToPatient.Self
          : insurance.relationToPatient),
      subscriberBirthSex: insurance.subscriberBirthSex,
      address1: insurance.address1,
      address2: insurance.address2,
      state: insurance.state,
      city: insurance.city,
      zipCode: insurance.zipCode,
    };
  };

  const cardShowHandler = (index: number, type: string) => {
    clearErrors();
    if (type === "expand") {
      if (index === 1) {
        setIsFirstCard(true);
        setIsSecondCard(false);
      } else {
        setIsSecondCard(true);
        setIsFirstCard(false);
      }
      setAddAnotherCard(false);
      setIsFormRemove(true);
    } else if (type === "add") {
      setIsSecondCard(false);
      setIsFirstCard(true);
      setAddAnotherCard(false);
      setIsFormRemove(true);
    } else {
      setIsSecondCard(false);
      setIsFirstCard(false);
      setExecuteOnce(false);
      if (!checkFormEmpty()) setAddAnotherCard(true);
      setIsFormRemove(false);
    }
  };

  const goToTop = () => undefined;

  const isPrimaryPolicyHolder = watch("isPrimaryPolicyHolder");

  React.useEffect(() => {
    if (isPrimaryPolicyHolder && !isShowFooter) {
      dispatch(setShowAddFooter(true));
    }
  }, [dispatch, isPrimaryPolicyHolder, isShowFooter]);

  React.useEffect(() => {
    if (isSubmit) {
      trigger();
    }
  }, [isSubmit, trigger]);

  return (
    <>
      {isFirstCard && (
        <div className={version && version === 2 ? styles.versionBG : ""}>
          <div
            className={`${insuranceFormDataArr[0]?.insuranceCarrier.length >= 20 ? styles.mobileExpandCustom : styles.mobileExpand} ${
              !isErrorFirstFields ? styles.errorExpand : ""
            }`}
          >
            <div className={insuranceFormDataArr[0]?.insuranceCarrier.length >= 6 ? styles.innerLeft : styles.innerLeftCustom}>
              <span>
                {insuranceFormDataArr[0]?.insuranceCarrier
                  ? insuranceFormDataArr[0]?.insuranceCarrier
                  : "Insurance 1"}
              </span>
              <a
                href={refStr}
                onClick={() => {
                  const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                  // eslint-disable-next-line array-callback-return
                  [getValues()].map((insurance: InsuranceFormData) => {
                    insuranceDataArr[1] = createInsuranceObj(insurance);
                  });
                  dispatch(setCurrentInsurance(0));
                  dispatch(setInsuranceData(insuranceDataArr));
                  getValidInsurance(insuranceDataArr);
                  setExecuteOnce(false);
                  setIsForm(false);
                  cardShowHandler(0, "expand");
                  goToTop();
                }}
              >
                Expand
              </a>
            </div>
            <div className={styles.innerRight}>
              <a
                href={refStr}
                onClick={(e) => {
                  const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                  insuranceArrTemp[0] = insuranceArrTemp[1];
                  insuranceArrTemp[1] = {};
                  dispatch(setInsuranceData(insuranceArrTemp));
                  dispatch(setCurrentInsurance(0));
                  setIsForm(false);
                  cardShowHandler(0, "remove");
                  goToTop();
                  e.preventDefault();
                }}
              >
                <img src={deleteIcon} alt="Remove" />
              </a>
            </div>
            {!isErrorFirstFields && (
              <div className={styles.errorFieldsMobile}>
                <img src={errorIcon} alt="Error" />
                <p>{errorMessageCard}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmInsuranceForm
        register={register}
        control={control}
        insuranceCarriers={insuranceCarriers}
        isRemoveBtn={false}
        setValue={setValue}
        errors={errors}
        watch={watch}
        onSubmit={handleSubmit(onNext)}
        formId={insuranceFormId}
        getInsuranceValues={getInsuranceValues1}
        getValues={getValues}
        currentPatient={currentPatient}
        version={version}
        alignSsnTooltipToInput={alignSsnTooltipToInput}
      />
      {addAnotherCard ? (
        <a
          className={styles.addInsurance}
          href={refStr}
          onClick={(e) => {
            e.preventDefault();

            if (isValid) {
              setErrorFirstFields(true);

              const insuranceDataArr = cloneDeep(insuranceFormDataArr);

              // eslint-disable-next-line array-callback-return
              [getValues()].map((insurance: InsuranceFormData) => {
                insuranceDataArr[0] = createInsuranceObj(insurance);
              });

              dispatch(setIsEditForm(false));
              dispatch(setCurrentInsurance(1));
              dispatch(setInsuranceData(insuranceDataArr));
              dispatch(setShowAddFooter(false));

              resetForm();
              cardShowHandler(1, "add");
              goToTop();
            } else {
              trigger();
            }
          }}
        >
          <div className={styles.insurance}>
            <h1 className={styles.header}>
              <img src={addCircle} alt="Add" />
              {isV3 ? "Add Another Insurance" : "ADD ANOTHER INSURANCE"}
            </h1>

            <span className={styles.addSpan}>
              MyEyeDr. will check and apply any additional Vision or Medical that gives you the greatest savings benefit.
            </span>
          </div>
        </a>

      ) : null}
      {isFormRemove && (
        <div className={styles.removeForm}>
          <br />
          <a
            className={styles.removeExpand}
            href={refStr}
            onClick={(e) => {
              const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
              const currentIndex = cloneDeep(currentInsurance);
              if (currentIndex === 1) {
                insuranceArrTemp[1] = {};
              } else if (currentIndex === 0) {
                insuranceArrTemp[0] = insuranceArrTemp[1];
                insuranceArrTemp[1] = {};
              }
              dispatch(setInsuranceData(insuranceArrTemp));
              dispatch(setCurrentInsurance(0));
              setIsForm(false);
              cardShowHandler(currentIndex, "inFormRemove");
              goToTop();
              e.preventDefault();
            }}
          >
            <img src={deleteIcon} alt="Remove" />
            Remove
          </a>
        </div>
      )}
      {isSecondCard && (
        <div className={version && version === 2 ? styles.versionBG : ""}>
          <div
            className={`${insuranceFormDataArr[1]?.insuranceCarrier.length >= 20 ? styles.mobileExpandCustom : styles.mobileExpand} ${
              styles.secondExpand} ${
              !isErrorSecondFields ? styles.errorExpand : ""
            }`}
          >
            <div className={insuranceFormDataArr[1]?.insuranceCarrier.length >= 6 ? styles.innerLeft : styles.innerLeftCustom}>
              <span>
                {insuranceFormDataArr[1]?.insuranceCarrier
                  ? insuranceFormDataArr[1]?.insuranceCarrier
                  : "Insurance 2"}
                  
              </span>
              <a
                className={insuranceFormDataArr[1]?.insuranceCarrier ? styles.expand : styles.expandCustom}
                href={refStr}
                onClick={() => {
                  dispatch(setCurrentInsurance(1));
                  setExecuteOnce(false);
                  setIsForm(false);
                  const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                  // eslint-disable-next-line array-callback-return
                  [getValues()].map((insurance: InsuranceFormData) => {
                    insuranceDataArr[0] = createInsuranceObj(insurance);
                  });
                  dispatch(setInsuranceData(insuranceDataArr));
                  getValidInsurance(insuranceDataArr);
                  cardShowHandler(1, "expand");
                  goToTop();
                }}
              >
                Expand
              </a>
            </div>
            <div className={styles.innerRight}>
              <a
                href={refStr}
                onClick={(e) => {
                  const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                  insuranceArrTemp[1] = {};
                  //delete insuranceArrTemp[currentIndex];
                  dispatch(setInsuranceData(insuranceArrTemp));
                  dispatch(setCurrentInsurance(0));
                  setIsForm(false);
                  cardShowHandler(1, "remove");
                  goToTop();
                  e.preventDefault();
                }}
              >
                <img src={deleteIcon} alt="Remove" />
              </a>
            </div>
            {!isErrorSecondFields && (
              <div className={isErrorSecondFields ? styles.errorFieldsMobile : styles.errorFieldsMobileCustom}>
                <img src={errorIcon} alt="Error" />
                <p>{errorMessageCard}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
